import { MealTime } from "@/clients/models/MealTime";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import {
  aiChatModalOpenAtom,
  chatMessagesAtom,
  chatOriginAtom,
  chatSessionIdAtom,
  pendingChatContextAtom,
  streamInMessageIdAtom,
} from "@/stores/chat.store";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { useUser } from "@clerk/clerk-react";
import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { buildMealCoachChat } from "../utils/build-meal-coach-chat";
import { MyPlanInteractionTracker, useMyPlanAnalyticsContext } from "./use-my-plan-analytics";

interface OpenMealCoachParams {
  /** Meal time the chat is about. Omit/null for a general day-level add (FAB /
   *  empty-state pill) with no specific slot. */
  mealTime?: MealTime | null;
  /** Plan day key, e.g. "tuesday". */
  day: string;
  /** ISO date (yyyy-MM-dd) for the selected plan day, when available. */
  targetDate?: string | null;
  /** Meal name when the slot is filled; omit/empty for an empty slot. */
  mealName?: string | null;
  mealId?: string | null;
}

/**
 * Opens the NutriCoach chat from a My Plan meal slot. Starts a fresh
 * conversation (new server session + transcript seeded with just the
 * contextual greeting) and stashes the meal context so it is prepended to the
 * user's first reply — see `pendingChatContextAtom`.
 */
export const useOpenMealCoach = (trackInteraction?: MyPlanInteractionTracker) => {
  const { user } = useUser();
  const setMessages = useSetAtom(chatMessagesAtom);
  const setSessionId = useSetAtom(chatSessionIdAtom);
  const setPendingContext = useSetAtom(pendingChatContextAtom);
  const setStreamInMessageId = useSetAtom(streamInMessageIdAtom);
  const setOrigin = useSetAtom(chatOriginAtom);
  const openAiChatModal = useSetAtom(aiChatModalOpenAtom);
  const myPlanAnalytics = useMyPlanAnalyticsContext();
  const trackMyPlanInteraction = trackInteraction ?? myPlanAnalytics?.trackInteraction;

  return useCallback(
    ({ mealTime, day, targetDate, mealName, mealId }: OpenMealCoachParams) => {
      const { greeting, contextPrefix } = buildMealCoachChat({
        mealTime,
        day,
        targetDate,
        firstName: user?.firstName,
        mealName,
        mealId,
      });

      const greetingId = crypto.randomUUID();
      setSessionId(crypto.randomUUID());
      setMessages([{ id: greetingId, role: "agent", text: greeting, traceId: null }]);
      setPendingContext(contextPrefix);
      // Fake-stream the greeting in when the chat mounts (see streamInMessageIdAtom).
      setStreamInMessageId(greetingId);
      setOrigin("meal");

      trackMixpanelEvent(MixpanelEvents.MY_PLAN_MEAL_COACH_TAPPED, {
        meal_time: mealTime ?? "any",
        has_meal: !!mealName,
      });
      trackMyPlanInteraction?.(mealTime ? "meal_coach_opened" : "add_meal_opened", {
        meal_time: mealTime ?? "any",
        has_meal: !!mealName,
      });

      openAiChatModal(true);
    },
    [
      user?.firstName,
      setMessages,
      setSessionId,
      setPendingContext,
      setStreamInMessageId,
      setOrigin,
      openAiChatModal,
      trackMyPlanInteraction,
    ],
  );
};
